import { useSyncExternalStore } from "react";
import {
  itemsApi,
  salesApi,
  cashApi,
  closedDaysApi,
  type ApiItem,
  type ApiSale,
  type ApiCashEntry,
  type ApiClosedDay,
  type ApiCashbook,
} from "./api/client";

export type Item = ApiItem;

export type Sale = ApiSale;

export type CashEntryType =
  | "purchase"
  | "expenditure"
  | "debt"
  | "debt_paid"
  | "mpesa_purchase";
export type CashEntry = ApiCashEntry;

export type DayMeta = {
  previousBalance: number;
};

/** A snapshot of a closed day, archived for history */
export type ClosedDay = ApiClosedDay;

type State = {
  items: Item[];
  sales: Sale[];
  cash: CashEntry[];
  meta: DayMeta;
  closedDays: ClosedDay[];
  cashbook: ApiCashbook | null;
};

const initial: State = {
  meta: { previousBalance: 20804 },
  items: [],
  sales: [],
  cash: [],
  closedDays: [],
  cashbook: null,
};

let state: State = initial;
const listeners = new Set<() => void>();

function set(updater: (s: State) => State) {
  state = updater(state);
  listeners.forEach((l) => l());
}

// ── API Data Fetching ────────────────────────────────────────────────────────

async function fetchAll() {
  try {
    const [items, sales, cash, closedDays, cashbook] = await Promise.all([
      itemsApi.list(),
      salesApi.list(),
      cashApi.list(),
      closedDaysApi.list(),
      cashApi.getCashbook(),
    ]);

    set((s) => ({
      ...s,
      items,
      sales,
      cash,
      closedDays,
      cashbook,
    }));
  } catch (err) {
    console.error("Failed to fetch store data:", err);
  }
}

export async function initStore() {
  await fetchAll();
}

// ── Store ───────────────────────────────────────────────────────────────────
export const store = {
  getState: () => state,
  subscribe: (l: () => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
  },

  /** Refresh all data from API */
  refresh: async () => {
    await fetchAll();
  },

  reset: () => {
    set(() => initial);
  },

  addSale: async (
    itemId: string,
    qty: number,
    payment: Sale["payment"],
    receiptId?: string,
  ) => {
    try {
      const sale = await salesApi.create({ itemId, qty, payment, receiptId });
      // Update local state immediately
      set((s) => ({
        ...s,
        sales: [sale, ...s.sales],
        items: s.items.map((i) =>
          i.id === itemId ? { ...i, stock: i.stock - qty } : i,
        ),
      }));
      return sale;
    } catch {
      return null;
    }
  },

  /** Get sales by receipt */
  getSalesByReceipt: (receiptId: string): Sale[] => {
    return state.sales.filter((s) => s.receiptId === receiptId);
  },

  /** Process a batch of cart items as one transaction with a shared receipt. */
  checkout: async (
    lines: { itemId: string; qty: number }[],
    payment: Sale["payment"],
  ): Promise<string | null> => {
    try {
      const result = await salesApi.checkout(lines, payment);
      // Refresh data
      await fetchAll();
      return result.receiptId;
    } catch {
      return null;
    }
  },

  addStock: async (
    itemId: string,
    qty: number,
    cost: number,
    payment?: "cash" | "mpesa" | "credit",
  ) => {
    try {
      await itemsApi.addStock(itemId, qty, cost, payment);
      await fetchAll();
    } catch (err) {
      console.error("Failed to add stock:", err);
    }
  },

  addItem: async (
    data: Omit<Item, "id" | "stock" | "openStock" | "addedStock">,
  ) => {
    try {
      await itemsApi.create({
        sku: data.sku,
        name: data.name,
        buyingPrice: data.buyingPrice,
        sellingPrice: data.sellingPrice,
      });
      await fetchAll();
    } catch (err) {
      console.error("Failed to add item:", err);
    }
  },

  addCash: async (type: CashEntryType, description: string, amount: number) => {
    try {
      const entry = await cashApi.add({ type, description, amount });
      set((s) => ({
        ...s,
        cash: [entry, ...s.cash],
      }));
      // Refresh cashbook
      const cashbook = await cashApi.getCashbook();
      set((s) => ({ ...s, cashbook }));
    } catch (err) {
      console.error("Failed to add cash entry:", err);
    }
  },

  setPreviousBalance: async (v: number) => {
    try {
      await cashApi.setPreviousBalance(v);
      set((s) => ({ ...s, meta: { ...s.meta, previousBalance: v } }));
    } catch (err) {
      console.error("Failed to set previous balance:", err);
    }
  },

  /** Close the current day, archive a snapshot, and reset for a new day. */
  closeDay: async (closedDate: string) => {
    try {
      const closedDay = await closedDaysApi.close(closedDate);
      set((s) => ({
        ...s,
        closedDays: [closedDay, ...s.closedDays],
        meta: { previousBalance: closedDay.bcf },
        sales: [],
        cash: [],
        items: s.items.map((i) => ({
          ...i,
          openStock: i.stock,
          addedStock: 0,
        })),
      }));
    } catch (err) {
      console.error("Failed to close day:", err);
    }
  },
};

// ── React hooks ──────────────────────────────────────────────────────────────
export function useStore<T>(selector: (s: State) => T): T {
  return useSyncExternalStore(
    store.subscribe,
    () => selector(state),
    () => selector(initial),
  );
}

export function useClosedDays() {
  return useStore((s) => s.closedDays);
}

// ── Computed helpers (client-side for immediate display) ────────────────────

export function getReportRows(s: State) {
  return s.items.map((item, idx) => {
    const itemSales = s.sales.filter((x) => x.itemId === item.id);
    const totalSale = itemSales.reduce((a, b) => a + Number(b.qty), 0);
    const totalAmount = itemSales.reduce((a, b) => a + Number(b.total), 0);
    const totalStock = Number(item.openStock) + Number(item.addedStock);
    const closeStock = totalStock - totalSale;
    const totalProfit =
      totalSale * (Number(item.sellingPrice) - Number(item.buyingPrice));
    return {
      idx: idx + 1,
      item: item.name,
      openStock: Number(item.openStock),
      addStock: Number(item.addedStock),
      totalStock,
      closeStock,
      totalSale,
      buyingPrice: Number(item.buyingPrice),
      salePrice: Number(item.sellingPrice),
      totalProfit,
      totalAmount,
      netProfit: totalProfit,
    };
  });
}

export function getCashbook(s: State) {
  const cashbook = s.cashbook;
  if (cashbook) return cashbook;

  // Fallback: compute locally
  const sum = (t: CashEntryType) =>
    s.cash
      .filter((c) => c.type === t)
      .reduce((a, b) => a + Number(b.amount), 0);
  const purchases = sum("purchase");
  const mpesaPurchases = sum("mpesa_purchase");
  const expenditure = sum("expenditure");
  const debts = sum("debt");
  const debtsPaid = sum("debt_paid");
  const salesTotal = s.sales.reduce((a, b) => a + Number(b.total), 0);
  const cashSales = s.sales
    .filter((x) => x.payment === "cash")
    .reduce((a, b) => a + Number(b.total), 0);
  const mpesaSales = s.sales
    .filter((x) => x.payment === "mpesa")
    .reduce((a, b) => a + Number(b.total), 0);
  const bankSales = s.sales
    .filter((x) => x.payment === "bank")
    .reduce((a, b) => a + Number(b.total), 0);
  const previousBalance = Number(s.meta.previousBalance);
  const total = previousBalance + salesTotal + debtsPaid;
  const banking = bankSales;
  const mpesa = mpesaSales + mpesaPurchases;
  const cashHandedOver = cashSales - purchases - expenditure;
  const bcf = total - purchases - expenditure - debts - banking - mpesa;
  return {
    purchases,
    expenditure,
    debts,
    debtsPaid,
    previousBalance,
    total,
    banking,
    mpesa,
    short: 0,
    cashHandedOver,
    bcf,
    salesTotal,
    cashSales,
    mpesaSales,
    bankSales,
  };
}
